import * as THREE from "three";
import { ConnectionGraph, PortId } from "./connections";
import { clampPlace, snapPos } from "./snap";
import type { ObjectField } from "./objects";
import type { EmitterField } from "./emitters";

interface ClipboardEdge {
  from: string;
  fromPort: PortId;
  to: string;
  toPort: PortId;
  param: string;
}

export interface Clipboard {
  objects: Record<string, unknown>[];
  emitters: Record<string, unknown>[];
  edges: ClipboardEdge[];
  basePoint: THREE.Vector3;
  entityIds: Set<string>;
}

/**
 * Collects rootId plus all recursive transform-out descendants (the clone set).
 * A visited guard handles any accidental cycles in the graph.
 */
export function collectCloneSet(rootId: string, graph: ConnectionGraph): Set<string> {
  const visited = new Set<string>();
  const queue: string[] = [rootId];
  while (queue.length > 0) {
    const id = queue.pop()!;
    if (visited.has(id)) {
      continue;
    }
    visited.add(id);
    for (const edge of graph.edgesFrom(id)) {
      if (edge.fromPort === "transformOut") {
        queue.push(edge.to);
      }
    }
  }
  return visited;
}

/**
 * Builds a Clipboard from the selected entity (+ its transform descendants).
 * Returns null if selectedId is not found in either field.
 */
export function buildClipboard(
  selectedId: string,
  graph: ConnectionGraph,
  field: ObjectField,
  emitterField: EmitterField,
): Clipboard | null {
  const entityIds = collectCloneSet(selectedId, graph);

  const rootObj = field.list().find((o) => o.id === selectedId);
  const rootEm = emitterField.list().find((e) => e.id === selectedId);
  if (!rootObj && !rootEm) {
    return null;
  }
  const basePoint = (rootObj?.position ?? rootEm!.position).clone();

  const objects: Record<string, unknown>[] = [];
  for (const entry of field.serialize() as unknown[]) {
    if (typeof entry === "object" && entry !== null) {
      const e = entry as Record<string, unknown>;
      if (typeof e.id === "string" && entityIds.has(e.id)) {
        objects.push(e);
      }
    }
  }

  const emitters: Record<string, unknown>[] = [];
  for (const entry of emitterField.serialize() as unknown[]) {
    if (typeof entry === "object" && entry !== null) {
      const e = entry as Record<string, unknown>;
      if (typeof e.id === "string" && entityIds.has(e.id)) {
        emitters.push(e);
      }
    }
  }

  const edges: ClipboardEdge[] = [];
  for (const edge of graph.all()) {
    if (entityIds.has(edge.from) || entityIds.has(edge.to)) {
      edges.push({
        from: edge.from,
        fromPort: edge.fromPort,
        to: edge.to,
        toPort: edge.toPort,
        param: edge.param,
      });
    }
  }

  return { objects, emitters, edges, basePoint, entityIds };
}

/**
 * Pastes the clipboard at dropPoint. New entities are offset from basePoint by
 * (dropPoint.x - basePoint.x, dropPoint.y - basePoint.y); z is preserved.
 * Each pasted entity's final position is snapped to the current move grid (same
 * idiom as a normal move). When grid is null (tests / internal callers) it keeps
 * the free-placement path. Every entity stores an absolute base
 * position — group/orbit parents included — so snapping each one independently
 * keeps the set consistent: grid-aligned originals all receive the same
 * quantized offset (relative layout preserved), off-grid originals are pulled
 * onto the grid as the user intends.
 * Internal edges get both endpoints remapped to new ids; boundary edges get only
 * the clone-set endpoint remapped (fan-in/fan-out increment). Edges that fail
 * canConnect (e.g. single-connection port already occupied on an external node)
 * are silently skipped — the caller should not bypass graph validation.
 * Returns the new entity ids in insertion order.
 */
export function pasteClipboard(
  cb: Clipboard,
  dropPoint: THREE.Vector3,
  graph: ConnectionGraph,
  field: ObjectField,
  emitterField: EmitterField,
  grid: number | null,
  is2D: boolean,
): string[] {
  const dx = dropPoint.x - cb.basePoint.x;
  const dy = dropPoint.y - cb.basePoint.y;

  const idMap = new Map<string, string>();
  for (const id of cb.entityIds) {
    idMap.set(id, crypto.randomUUID());
  }

  const newIds: string[] = [];

  for (const entry of cb.objects) {
    const newId = idMap.get(entry.id as string)!;
    const base = new THREE.Vector3((entry.x as number) + dx, (entry.y as number) + dy, entry.z as number);
    const pos = grid === null ? base : snapPos(base, grid, is2D);
    clampPlace(pos, is2D);
    field.restore([{ ...entry, id: newId, x: pos.x, y: pos.y, z: pos.z }]);
    newIds.push(newId);
  }

  for (const entry of cb.emitters) {
    const newId = idMap.get(entry.id as string)!;
    const base = new THREE.Vector3((entry.x as number) + dx, (entry.y as number) + dy, entry.z as number);
    const pos = grid === null ? base : snapPos(base, grid, is2D);
    clampPlace(pos, is2D);
    emitterField.restore([{ ...entry, id: newId, x: pos.x, y: pos.y, z: pos.z }]);
    newIds.push(newId);
  }

  for (const edge of cb.edges) {
    const newFrom = idMap.get(edge.from) ?? edge.from;
    const newTo = idMap.get(edge.to) ?? edge.to;
    const lenBefore = graph.all().length;
    const ok = graph.connect({ from: newFrom, fromPort: edge.fromPort, to: newTo, toPort: edge.toPort });
    if (ok && edge.param !== "" && graph.all().length === lenBefore + 1) {
      graph.setEdgeParam(graph.all()[graph.all().length - 1].id, edge.param);
    }
  }

  return newIds;
}
